// === IMPORTS ===
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  OfficeLayout,
  AIAgent,
  TaskItem,
  WorkflowPipeline,
  SimulationStats,
  AgentStatus,
  PlacedFurniture,
  InteractionTypeDefinition,
  TimeMachineEvent,
  TimeMachineIncident,
  AgentInfoCategory,
} from '../types';
import { DEFAULT_WORKFLOWS, AGENT_ROLES_CONFIG, FURNITURE_CATALOG, DEFAULT_INTERACTION_TYPES } from '../data/officeCatalog';
import { findGridPath } from '../utils/isoMath';
import { getIsometric4Direction } from '../utils/agentSpriteEngine';
import { AgentModal } from './AgentModal';
import { InteractionManagerModal } from './InteractionManagerModal';
import { Play, AlertTriangle, Zap, Clock, TrendingUp, CheckCircle2, Workflow, BarChart3, History, Users } from 'lucide-react';
import './OfficeSimulation.css';

interface OfficeSimulationProps {
  layout: OfficeLayout;
  onLayoutUpdate: (updatedLayout: OfficeLayout) => void;
  selectedAgent?: AIAgent | null;
  initialAgentTab?: AgentInfoCategory;
  visualThemeId: string;
  onSelectAgentModal: (agent: AIAgent | null, initialTab?: AgentInfoCategory) => void;
  onSwitchToEditor: () => void;
  interactionTypes?: InteractionTypeDefinition[];
  isTimeMachineReplay?: boolean;
  timeMachineClockTime?: string;
  historicalStats?: SimulationStats;
  historicalEvents?: TimeMachineEvent[];
  activeIncident?: TimeMachineIncident | null;
  onOpenInspector?: (incident?: TimeMachineIncident) => void;
  onGoLive?: () => void;
  onJumpToIncident?: (incident: TimeMachineIncident) => void;
  incidentsList?: TimeMachineIncident[];
  isPlaying?: boolean;
  onTogglePlayPause?: () => void;
}

// === COMPONENTE: PANEL DE SIMULACIÓN EN VIVO (KPIs, WORKFLOWS, ROSTER, LOG) ===
export const OfficeSimulation: React.FC<OfficeSimulationProps> = ({
  layout,
  onLayoutUpdate,
  selectedAgent: externalSelectedAgent,
  initialAgentTab,
  onSelectAgentModal,
  interactionTypes,
  visualThemeId,
  isTimeMachineReplay = false,
  historicalStats,
  onJumpToIncident,
  incidentsList = [],
  isPlaying: externalIsPlaying,
  onTogglePlayPause,
}) => {
  // Simulation Controls State
  const [internalIsPlaying, setInternalIsPlaying] = useState<boolean>(true);
  const isPlaying = externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
  const setIsPlaying = (val: boolean | ((prev: boolean) => boolean)) => {
    if (onTogglePlayPause && typeof val === 'boolean' && val !== isPlaying) {
      onTogglePlayPause();
    } else if (onTogglePlayPause && typeof val === 'function') {
      onTogglePlayPause();
    }
    setInternalIsPlaying(val);
  };
  const [speed, setSpeed] = useState<number>(1);
  const [internalSelectedAgent, setInternalSelectedAgent] = useState<AIAgent | null>(null);
  const [isProcessingAI, setIsProcessingAI] = useState<boolean>(false);
  const [showInteractionsGuide, setShowInteractionsGuide] = useState<boolean>(false);

  const selectedAgent = externalSelectedAgent !== undefined ? externalSelectedAgent : internalSelectedAgent;
  const setSelectedAgent = (agent: AIAgent | null, initialTab?: AgentInfoCategory) => {
    setInternalSelectedAgent(agent);
    onSelectAgentModal(agent, initialTab);
  };

  // Keep fresh layout ref to avoid stale closures in interval
  const layoutRef = useRef<OfficeLayout>(layout);
  layoutRef.current = layout;

  // Active interaction definitions (custom or defaults)
  const activeInteractions = interactionTypes || DEFAULT_INTERACTION_TYPES;
  const activeInteractionsRef = useRef<InteractionTypeDefinition[]>(activeInteractions);
  activeInteractionsRef.current = activeInteractions;

  // Active Workflows
  const [workflows, setWorkflows] = useState<WorkflowPipeline[]>(DEFAULT_WORKFLOWS);

  // Performance KPI Metrics
  const [stats, setStats] = useState<SimulationStats>({
    totalTasksCompleted: 42,
    tasksInQueue: 3,
    throughputTasksPerMin: 14.5,
    avgSlaLatencySec: 2.3,
    officeEfficiencyPct: 92,
    activeBottlenecks: [],
    history: [],
  });

  // Activity Log feed
  const [activityLogs, setActivityLogs] = useState<
    { id: string; time: string; text: string; type: 'task' | 'break' | 'bottleneck' | 'ai' }[]
  >([
    {
      id: 'l1',
      time: new Date().toLocaleTimeString(),
      text: 'Simulation started. Active agents at their workstations.',
      type: 'task',
    },
  ]);

  const addLog = useCallback((text: string, type: 'task' | 'break' | 'bottleneck' | 'ai') => {
    setActivityLogs((prev) => [
      {
        id: `log_${Date.now()}_${Math.random()}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        text,
        type,
      },
      ...prev.slice(0, 24),
    ]);
  }, []);

  // === CICLO PRINCIPAL DE LA SIMULACIÓN ===
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      const currentLayout = layoutRef.current;
      const currentInteractions = activeInteractionsRef.current;
      const pendingLogs: { text: string; type: 'task' | 'break' | 'bottleneck' | 'ai' }[] = [];
      let updatedCompletedCount = 0;
      const newBottlenecks: { agentName: string; reason: string; queueLength: number }[] = [];

      const updatedAgents = currentLayout.agents.map((agent) => {
        if (agent.isPlaced === false || agent.currentCol < 0 || agent.currentRow < 0) {
          return agent;
        }

        let updated = { ...agent };

        // 1. Walking along path
        if (updated.path.length > 0) {
          updated.status = 'walking';
          const nextStep = updated.path[0];

          const dx = nextStep.col - updated.currentCol;
          const dy = nextStep.row - updated.currentRow;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist > 0.01) {
            updated.facingDirection = getIsometric4Direction(dx, dy, updated.facingDirection || 'front');
          }

          const moveStep = 0.25 * speed;
          const actualStep = Math.min(dist, moveStep);
          const tileSize = currentLayout.tileSizeMeters ?? 1.0;
          updated.distanceTraveledMeters = (updated.distanceTraveledMeters || 0) + actualStep * tileSize;

          if (dist <= moveStep) {
            updated.currentCol = nextStep.col;
            updated.currentRow = nextStep.row;
            updated.path = updated.path.slice(1);
          } else {
            updated.currentCol += (dx / dist) * moveStep;
            updated.currentRow += (dy / dist) * moveStep;
          }

          return updated;
        }

        // 2. State Machine behavior when arrived
        if (updated.status === 'coffee_break') {
          const nearbyItem = currentLayout.items.find(
            (i) => Math.abs(i.col - updated.currentCol) <= 1.5 && Math.abs(i.row - updated.currentRow) <= 1.5
          );
          const furnDef = nearbyItem ? FURNITURE_CATALOG.find((f) => f.type === nearbyItem.type) : null;
          const intType = furnDef?.interactionType || 'coffee';
          const intDef = currentInteractions.find((i) => i.id === intType);
          const b = intDef?.behavior;

          const energyGain = b ? b.energyDelta : 8;
          const focusGain = b ? b.focusDelta : 5;
          const bladderDelta = b ? b.bladderDelta : 0.5;

          updated.energy = Math.min(Math.max(updated.energy + energyGain * speed, 0), 100);
          updated.focus = Math.min(Math.max(updated.focus + focusGain * speed, 0), 100);
          if (bladderDelta !== 0) {
            updated.bladder = Math.min(Math.max(updated.bladder + bladderDelta * speed, 0), 100);
          }

          if (updated.energy >= 95) {
            updated.status = 'working';
            const desk = currentLayout.items.find((i) => i.id === updated.assignedDeskId);
            if (desk) {
              updated.path = findGridPath(
                { col: Math.round(updated.currentCol), row: Math.round(updated.currentRow) },
                { col: desk.col, row: desk.row + 1 },
                currentLayout.gridWidth,
                currentLayout.gridHeight,
                currentLayout.items
              );
            }
            pendingLogs.push({
              text: `${intDef?.icon || '☕'} ${updated.name} finished break and returned to desk.`,
              type: 'break',
            });
          }
        } else if (updated.status === 'bathroom_break') {
          const nearbyItem = currentLayout.items.find(
            (i) => Math.abs(i.col - updated.currentCol) <= 1.5 && Math.abs(i.row - updated.currentRow) <= 1.5
          );
          const furnDef = nearbyItem ? FURNITURE_CATALOG.find((f) => f.type === nearbyItem.type) : null;
          const intType = furnDef?.interactionType || 'toilet';
          const intDef = currentInteractions.find((i) => i.id === intType);
          const b = intDef?.behavior;

          const bladderReduction = b && b.bladderDelta < 0 ? Math.abs(b.bladderDelta) : 15;
          updated.bladder = Math.max(updated.bladder - bladderReduction * speed, 0);

          if (updated.bladder <= 5) {
            updated.status = 'working';
            const desk = currentLayout.items.find((i) => i.id === updated.assignedDeskId);
            if (desk) {
              updated.path = findGridPath(
                { col: Math.round(updated.currentCol), row: Math.round(updated.currentRow) },
                { col: desk.col, row: desk.row + 1 },
                currentLayout.gridWidth,
                currentLayout.gridHeight,
                currentLayout.items
              );
            }
            pendingLogs.push({
              text: `${intDef?.icon || '🚽'} ${updated.name} finished restroom break and returned to work.`,
              type: 'break',
            });
          }
        } else {
          updated.energy = Math.max(updated.energy - 0.4 * speed, 0);
          updated.bladder = Math.min(updated.bladder + 0.6 * speed, 100);

          if (updated.energy < 25) {
            const energizingSpot = currentLayout.items.find((item) => {
              const fDef = FURNITURE_CATALOG.find((f) => f.type === item.type);
              if (!fDef || !fDef.interactionType || fDef.interactionType === 'none') return false;
              const iDef = currentInteractions.find((i) => i.id === fDef.interactionType);
              return (
                (iDef && iDef.behavior.energyDelta > 0) ||
                fDef.interactionType === 'coffee' ||
                fDef.interactionType === 'water' ||
                fDef.interactionType === 'rest'
              );
            });

            if (energizingSpot) {
              const fDef = FURNITURE_CATALOG.find((f) => f.type === energizingSpot.type);
              const iDef = currentInteractions.find((i) => i.id === fDef?.interactionType);
              updated.status = 'coffee_break';
              updated.path = findGridPath(
                { col: Math.round(updated.currentCol), row: Math.round(updated.currentRow) },
                { col: energizingSpot.col, row: energizingSpot.row },
                currentLayout.gridWidth,
                currentLayout.gridHeight,
                currentLayout.items
              );
              pendingLogs.push({
                text: `${iDef?.icon || '☕'} ${updated.name} is low on energy and went to ${fDef?.name || 'recharge'}.`,
                type: 'break',
              });
            }
          } else if (updated.bladder > 80) {
            const toiletSpot = currentLayout.items.find((item) => {
              const fDef = FURNITURE_CATALOG.find((f) => f.type === item.type);
              if (!fDef || !fDef.interactionType || fDef.interactionType === 'none') return false;
              const iDef = currentInteractions.find((i) => i.id === fDef.interactionType);
              return (iDef && iDef.behavior.bladderDelta < 0) || fDef.interactionType === 'toilet';
            });

            if (toiletSpot) {
              const fDef = FURNITURE_CATALOG.find((f) => f.type === toiletSpot.type);
              const iDef = currentInteractions.find((i) => i.id === fDef?.interactionType);
              updated.status = 'bathroom_break';
              updated.path = findGridPath(
                { col: Math.round(updated.currentCol), row: Math.round(updated.currentRow) },
                { col: toiletSpot.col, row: toiletSpot.row },
                currentLayout.gridWidth,
                currentLayout.gridHeight,
                currentLayout.items
              );
              pendingLogs.push({
                text: `${iDef?.icon || '🚽'} ${updated.name} stepped away to ${fDef?.name || 'the restroom'}.`,
                type: 'break',
              });
            }
          }

          if (updated.status === 'working' && updated.taskQueue.length > 0) {
            const currentTask = { ...updated.taskQueue[0] };
            currentTask.progress += 8 * speed;

            if (currentTask.progress >= 100) {
              updatedCompletedCount++;
              updated.processedTasksCount++;
              updated.taskQueue = updated.taskQueue.slice(1);
              pendingLogs.push({
                text: `✅ ${updated.name} completed task "${currentTask.title}".`,
                type: 'task',
              });
            } else {
              updated.taskQueue = [currentTask, ...updated.taskQueue.slice(1)];
            }
          }
        }

        if (
          (updated.status === 'coffee_break' || updated.status === 'bathroom_break') &&
          updated.taskQueue.length > 0
        ) {
          newBottlenecks.push({
            agentName: updated.name,
            reason: updated.status === 'coffee_break' ? 'At break room' : 'In restroom',
            queueLength: updated.taskQueue.length,
          });
        }

        return updated;
      });

      onLayoutUpdate({ ...currentLayout, agents: updatedAgents });

      if (pendingLogs.length > 0) {
        setActivityLogs((prev) => {
          const newEntries = pendingLogs.map((pl) => ({
            id: `log_${Date.now()}_${Math.random()}`,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            text: pl.text,
            type: pl.type,
          }));
          return [...newEntries, ...prev].slice(0, 25);
        });
      }

      setStats((prevStats) => {
        const breakCount = updatedAgents.filter(
          (a) => a.status === 'coffee_break' || a.status === 'bathroom_break'
        ).length;
        const totalQueue = updatedAgents.reduce((sum, a) => sum + a.taskQueue.length, 0);
        const activeCount = updatedAgents.length - breakCount;
        const efficiency = Math.round((activeCount / Math.max(updatedAgents.length, 1)) * 100);

        return {
          ...prevStats,
          totalTasksCompleted: prevStats.totalTasksCompleted + updatedCompletedCount,
          tasksInQueue: totalQueue,
          officeEfficiencyPct: efficiency,
          activeBottlenecks: newBottlenecks,
          throughputTasksPerMin: Number((12 + activeCount * 1.5).toFixed(1)),
          avgSlaLatencySec: Number((1.8 + breakCount * 0.9).toFixed(1)),
        };
      });
    }, 1000 / (2 * speed));

    return () => clearInterval(interval);
  }, [isPlaying, speed, onLayoutUpdate]);

  // Force Agent Action
  const handleForceBreak = (agentId: string, type: 'coffee' | 'bathroom' | 'work') => {
    onLayoutUpdate({
      ...layout,
      agents: layout.agents.map((ag) => {
        if (ag.id !== agentId) return ag;

        let newStatus: AgentStatus = 'working';
        let targetItem: PlacedFurniture | undefined;

        if (type === 'coffee') {
          newStatus = 'coffee_break';
          targetItem = layout.items.find((i) => i.type === 'coffee_station' || i.type === 'water_dispenser');
        } else if (type === 'bathroom') {
          newStatus = 'bathroom_break';
          targetItem = layout.items.find((i) => i.type === 'toilet' || i.type === 'bathroom_stall');
        } else {
          newStatus = 'working';
          targetItem = layout.items.find((i) => i.id === ag.assignedDeskId);
        }

        const newPath = targetItem
          ? findGridPath(
              { col: Math.round(ag.currentCol), row: Math.round(ag.currentRow) },
              { col: targetItem.col, row: targetItem.row },
              layout.gridWidth,
              layout.gridHeight,
              layout.items
            )
          : [];

        return { ...ag, status: newStatus, path: newPath };
      }),
    });

    addLog(`⚡ Manual order executed on ${layout.agents.find((a) => a.id === agentId)?.name}.`, 'break');
  };

  // Trigger Preset Workflow Pipeline
  const handleTriggerWorkflow = (pipeline: WorkflowPipeline) => {
    addLog(`🚀 Workflow pipeline triggered: "${pipeline.name}"`, 'task');

    const step1 = pipeline.steps[0];
    const targetAgent = layout.agents.find((a) => a.role === step1.role);

    if (targetAgent) {
      const newTask: TaskItem = {
        id: `task_${Date.now()}`,
        title: step1.taskTitle,
        type: pipeline.name,
        assignedAgentId: targetAgent.id,
        progress: 0,
        createdAt: Date.now(),
        complexity: step1.complexity,
        status: 'in_progress',
        workflowId: pipeline.id,
        stepIndex: 0,
      };

      onLayoutUpdate({
        ...layout,
        agents: layout.agents.map((a) => (a.id === targetAgent.id ? { ...a, taskQueue: [...a.taskQueue, newTask] } : a)),
      });

      addLog(`📋 Task "${step1.taskTitle}" queued for ${targetAgent.name}.`, 'task');
    }
  };

  // === EJECUTAR TAREA PERSONALIZADA (llama al backend, que a su vez usa Gemini AI) ===
  const handleRunCustomAITask = async (agentId: string, taskTitle: string, context: string) => {
    const agent = layout.agents.find((a) => a.id === agentId);
    if (!agent) return;

    setIsProcessingAI(true);
    addLog(`🧠 ${agent.name} processing response with Gemini AI...`, 'ai');

    try {
      const res = await fetch('/api/agent/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentName: agent.name,
          role: AGENT_ROLES_CONFIG[agent.role]?.title,
          systemPrompt: agent.systemPrompt,
          taskTitle,
          context,
        }),
      });

      const data = await res.json();
      const outputText = data.output || 'Task completed with no output provided.';

      onLayoutUpdate({
        ...layout,
        agents: layout.agents.map((a) =>
          a.id === agentId
            ? {
                ...a,
                lastOutput: outputText,
                processedTasksCount: a.processedTasksCount + 1,
                thoughts: [
                  ...a.thoughts,
                  {
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    text: `Completed: ${taskTitle}`,
                    mood: 'happy',
                  },
                ],
              }
            : a
        ),
      });

      addLog(`✨ Gemini AI: ${agent.name} generated real-time response.`, 'ai');
    } catch (err) {
      console.error('AI execution error:', err);
      addLog(`❌ Error executing AI task on ${agent.name}`, 'bottleneck');
    } finally {
      setIsProcessingAI(false);
    }
  };

  const displayedStats = isTimeMachineReplay && historicalStats ? historicalStats : stats;

  // === RENDER ===
  return (
    <div className="twin-simulation">
      {/* === PANEL DE KPIs === */}
      <div className="twin-simulation-kpi-grid">
        <div className="twin-simulation-kpi-card">
          <div className="twin-simulation-kpi-header">
            <span>Throughput</span>
            <TrendingUp className="twin-icon-xs twin-color-emerald" />
          </div>
          <p className="twin-simulation-kpi-value twin-simulation-kpi-value--emerald">
            {displayedStats.throughputTasksPerMin} <span className="twin-simulation-kpi-unit">tasks/min</span>
          </p>
        </div>

        <div className="twin-simulation-kpi-card">
          <div className="twin-simulation-kpi-header">
            <span>SLA Latency</span>
            <Clock className="twin-icon-xs twin-color-amber" />
          </div>
          <p className="twin-simulation-kpi-value twin-simulation-kpi-value--amber">{displayedStats.avgSlaLatencySec}s</p>
        </div>

        <div className="twin-simulation-kpi-card">
          <div className="twin-simulation-kpi-header">
            <span>Office Efficiency</span>
            <Zap className="twin-icon-xs twin-color-cyan" />
          </div>
          <p className="twin-simulation-kpi-value twin-simulation-kpi-value--cyan">{displayedStats.officeEfficiencyPct}%</p>
        </div>

        <div className="twin-simulation-kpi-card">
          <div className="twin-simulation-kpi-header">
            <span>Completed</span>
            <CheckCircle2 className="twin-icon-xs twin-color-blue" />
          </div>
          <p className="twin-simulation-kpi-value twin-simulation-kpi-value--blue">{displayedStats.totalTasksCompleted}</p>
        </div>
      </div>

      {/* === BANNER DE CUELLO DE BOTELLA === */}
      {displayedStats.activeBottlenecks.length > 0 && (
        <div className="twin-simulation-bottleneck-banner">
          <AlertTriangle className="twin-icon-sm twin-simulation-bottleneck-icon" />
          <div>
            <p className="twin-simulation-bottleneck-title">⚠️ Bottleneck Detected</p>
            {displayedStats.activeBottlenecks.map((b, i) => (
              <p key={i} className="twin-simulation-bottleneck-item">
                • {b.agentName} is {b.reason.toLowerCase()} with {b.queueLength} pending tasks at their desk.
              </p>
            ))}
          </div>
        </div>
      )}

      {/* === TIME MACHINE: SALTOS RÁPIDOS === */}
      <div className="twin-simulation-timemachine-card">
        <div className="twin-simulation-timemachine-header">
          <span className="twin-simulation-timemachine-title">
            <History className="twin-icon-xs twin-color-amber" />
            Time Machine / AI System DVR
          </span>
          <span className={`twin-simulation-timemachine-badge ${isTimeMachineReplay ? 'twin-simulation-timemachine-badge--replay' : 'twin-simulation-timemachine-badge--live'}`}>
            {isTimeMachineReplay ? 'HISTORICAL REPLAY' : 'LIVE'}
          </span>
        </div>

        <div className="twin-simulation-incident-list">
          {incidentsList.map((inc) => (
            <button key={inc.id} onClick={() => onJumpToIncident && onJumpToIncident(inc)} className="twin-simulation-incident-btn">
              <span className="twin-simulation-incident-time">[{inc.clockTime}]</span>
              <div className="twin-simulation-incident-text">
                <p className="twin-simulation-incident-title">{inc.title}</p>
                <p className="twin-simulation-incident-desc">Reconstruct: Task ➔ Chat ➔ MCP Server ➔ Error 500</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* === CUERPO PRINCIPAL === */}
      <div className="twin-simulation-body">
        {/* === WORKFLOWS === */}
        <div className="twin-simulation-section">
          <h3 className="twin-simulation-section-title">
            <Workflow className="twin-icon-xs twin-color-cyan" /> Trigger Workflows
          </h3>

          <div className="twin-simulation-workflow-list">
            {workflows.map((pipe) => (
              <div key={pipe.id} className="twin-simulation-workflow-card">
                <div className="twin-simulation-workflow-top">
                  <span className="twin-simulation-workflow-name">{pipe.name}</span>
                  <button onClick={() => handleTriggerWorkflow(pipe)} className="twin-simulation-workflow-run-btn">
                    <Play className="twin-icon-xxs" /> Run
                  </button>
                </div>

                <p className="twin-simulation-workflow-desc">{pipe.description}</p>

                <div className="twin-simulation-workflow-steps">
                  {pipe.steps.map((st, idx) => (
                    <React.Fragment key={idx}>
                      <span className="twin-simulation-workflow-step-chip">{st.role}</span>
                      {idx < pipe.steps.length - 1 && <span className="twin-simulation-workflow-step-arrow">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* === ROSTER DE AGENTES EN VIVO === */}
        <div className="twin-simulation-section">
          <h3 className="twin-simulation-section-title twin-simulation-section-title--between">
            <span className="twin-simulation-section-title-left">
              <Users className="twin-icon-xs twin-color-theme-accent" />
              <span className="twin-simulation-section-title-text">Agent Roster ({layout.agents.length})</span>
            </span>
            <span className="twin-simulation-section-hint">Click to open dossier</span>
          </h3>

          <div className="twin-simulation-agent-list">
            {layout.agents.map((ag) => {
              const roleConfig = AGENT_ROLES_CONFIG[ag.role];
              const isPlaced = ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0;
              const isBreak = isPlaced && (ag.status === 'coffee_break' || ag.status === 'bathroom_break');

              return (
                <div
                  key={ag.id}
                  onClick={() => setSelectedAgent(ag)}
                  className={`twin-simulation-agent-card ${!isPlaced ? 'twin-simulation-agent-card--unplaced' : isBreak ? 'twin-simulation-agent-card--break' : ''}`}
                >
                  <div className="twin-simulation-agent-left">
                    <div className="twin-simulation-agent-avatar" style={{ backgroundColor: roleConfig?.avatarShirtColor || '#3b82f6' }}>
                      {ag.spriteSet?.front_idle || ag.customImage ? (
                        <img
                          src={ag.spriteSet?.front_idle || ag.customImage}
                          alt={ag.name}
                          className="twin-simulation-agent-avatar-img"
                          style={{ imageRendering: 'pixelated' }}
                        />
                      ) : (
                        roleConfig?.icon || '🤖'
                      )}
                    </div>

                    <div className="twin-simulation-agent-info">
                      <p className="twin-simulation-agent-name">
                        {ag.nickname?.trim() || ag.name}
                        {ag.taskQueue.length > 0 && <span className="twin-simulation-agent-task-badge">{ag.taskQueue.length} tasks</span>}
                      </p>

                      <p className="twin-simulation-agent-meta">
                        {isPlaced ? (
                          <>
                            <span>⚡ {Math.round(ag.energy)}%</span>
                            <span>🚽 {Math.round(ag.bladder)}%</span>
                            <span>
                              📍 ({Math.round(ag.currentCol)}, {Math.round(ag.currentRow)})
                            </span>
                          </>
                        ) : (
                          <span className="twin-simulation-agent-meta-unplaced">Available in roster (unplaced)</span>
                        )}
                        {ag.nickname && ag.nickname !== ag.name && <span className="twin-simulation-agent-meta-realname">({ag.name})</span>}
                      </p>
                    </div>
                  </div>

                  <div className="twin-simulation-agent-status-wrap">
                    <span
                      className={`twin-simulation-agent-status-pill ${
                        !isPlaced
                          ? 'twin-simulation-agent-status-pill--unplaced'
                          : ag.status === 'coffee_break'
                          ? 'twin-simulation-agent-status-pill--coffee'
                          : ag.status === 'bathroom_break'
                          ? 'twin-simulation-agent-status-pill--bathroom'
                          : ag.status === 'walking'
                          ? 'twin-simulation-agent-status-pill--walking'
                          : 'twin-simulation-agent-status-pill--working'
                      }`}
                    >
                      {!isPlaced
                        ? '⏳ Unplaced'
                        : ag.status === 'coffee_break'
                        ? '☕ Break Room'
                        : ag.status === 'bathroom_break'
                        ? '🚽 Restroom'
                        : ag.status === 'walking'
                        ? '🚶 In Transit'
                        : '💻 Working'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* === LOG DE ACTIVIDAD EN VIVO === */}
        <div className="twin-simulation-section">
          <h3 className="twin-simulation-section-title">
            <BarChart3 className="twin-icon-xs twin-color-amber" /> Activity &amp; Movement Log
          </h3>

          <div className="twin-simulation-log-box">
            {activityLogs.map((l) => (
              <div key={l.id} className="twin-simulation-log-row">
                <span className="twin-simulation-log-time">[{l.time}]</span>
                <span
                  className={`twin-simulation-log-text ${
                    l.type === 'break'
                      ? 'twin-simulation-log-text--break'
                      : l.type === 'ai'
                      ? 'twin-simulation-log-text--ai'
                      : l.type === 'bottleneck'
                      ? 'twin-simulation-log-text--bottleneck'
                      : ''
                  }`}
                >
                  {l.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* === MODAL DE DETALLE DE AGENTE === */}
      {selectedAgent && createPortal(
        <div className="twin-root" data-theme={visualThemeId} style={{ display: 'contents' }}>
          <AgentModal
          agent={layout.agents.find((a) => a.id === selectedAgent.id) || selectedAgent}
          allAgents={layout.agents}
          initialTab={initialAgentTab}
          onClose={() => setSelectedAgent(null)}
          onForceBreak={handleForceBreak}
          onRunCustomTask={handleRunCustomAITask}
          isProcessingAI={isProcessingAI}
        />
        </div>,
        document.body
      )}

      {/* === MODAL: GESTOR DE INTERACCIONES Y AYUDA === */}
      <InteractionManagerModal
        isOpen={showInteractionsGuide}
        onClose={() => setShowInteractionsGuide(false)}
        interactions={activeInteractions}
        layout={layout}
        initialTab="guide"
        onSaveInteraction={() => {}}
        onDeleteInteraction={() => {}}
        />
    </div>
  );
};